/**
 * The three documents somebody agrees to at sign-up.
 *
 * Here rather than on a separate page, and shown in a dialog rather than behind a link that
 * navigates away: a form somebody has half-filled is a form they lose by leaving it, and a
 * consent nobody could read without abandoning their work is a consent in name only.
 *
 * SJS Tech is the company and the data controller's counterparty; ClientMa is the platform and
 * the applications on it. Keeping those apart matters in a document of this kind — the customer
 * contracts with SJS Tech, and what they are licensed to use is ClientMa.
 *
 * These are a plain-language summary written to be read, not a substitute for counsel. The
 * placeholders a real deployment must fill in — registered address, registration number, the
 * data protection officer's contact — are marked so nobody publishes them by accident.
 */

export interface LegalDocument {
  key: string;
  title: string;
  updated: string;
  intro: string;
  sections: { heading: string; body: string[] }[];
}

const PROVIDER = 'SJS Tech';
const PRODUCT = 'ClientMa';

/** Shown once at the top of the dialog, because it applies to all three. */
export const legalPreamble =
  `${PROVIDER} is the company you contract with. ${PRODUCT} is the platform it provides and the `
  + 'applications on it — CRM, SRM, Inventory and whatever else you subscribe to. Where these '
  + 'documents say "we", they mean ' + PROVIDER + '; where they say "the service", they mean the '
  + PRODUCT + ' applications your organisation has subscribed to.';

export const legalDocuments: LegalDocument[] = [
  {
    key: 'terms',
    title: 'Terms of service',
    updated: '26 August 2026',
    intro:
      `These terms govern your organisation's use of ${PRODUCT}. Accepting them at sign-up `
      + 'creates an agreement between your organisation and ' + PROVIDER + '.',
    sections: [
      {
        heading: 'What you are subscribing to',
        body: [
          `Each ${PRODUCT} application is subscribed to separately, with its own seats. A trial `
          + 'runs for the period shown on the plan and needs no payment card. Nothing is charged '
          + 'until the trial ends and you choose to continue.',
          'Your subscription is per named user. A seat may be reassigned when somebody leaves; '
          + 'it may not be shared between two people at once.',
        ],
      },
      {
        heading: 'What we undertake',
        body: [
          'To provide the applications you have subscribed to with reasonable skill and care, to '
          + 'keep your data separated from every other customer’s, and to tell you about '
          + 'planned maintenance that will interrupt the service.',
          'To keep your data available to you throughout your subscription and for a defined '
          + 'period after it ends, so that you can export it.',
        ],
      },
      {
        heading: 'What you undertake',
        body: [
          'To keep your credentials confidential, to use the service lawfully, and not to attempt '
          + 'to reach another customer’s data or to test the platform’s security '
          + 'without our written agreement.',
          'To hold the rights you need in the data you put into the service, and to have a lawful '
          + 'basis for the personal data you ask us to process on your behalf.',
        ],
      },
      {
        heading: 'Fees, renewal and ending it',
        body: [
          'Fees are those shown on the plan at the time you subscribe, in the currency of your '
          + 'billing country, exclusive of tax unless stated. A subscription renews for the same '
          + 'term unless you cancel before the term ends.',
          'You may cancel at any time; the subscription then ends at the end of the term you have '
          + 'paid for rather than immediately. We may suspend a subscription that is unpaid after '
          + 'the grace period shown on your invoice.',
        ],
      },
      {
        heading: 'Liability',
        body: [
          'Neither party excludes liability for death or personal injury caused by negligence, '
          + 'for fraud, or for anything else that cannot lawfully be excluded.',
          'Subject to that, our total liability in any twelve-month period is limited to the fees '
          + 'paid for the affected application in that period. Neither party is liable for '
          + 'indirect or consequential loss.',
        ],
      },
      {
        heading: 'Governing law',
        body: [
          '[Placeholder — a real deployment must state the governing law and the courts having '
          + 'jurisdiction, which differ for the Singapore, Malaysian and Indian entities.]',
        ],
      },
    ],
  },

  {
    key: 'privacy',
    title: 'Privacy policy',
    updated: '26 August 2026',
    intro:
      `How ${PROVIDER} handles personal data — both the data about you as a user of ${PRODUCT}, `
      + 'and the data your organisation puts into it about other people.',
    sections: [
      {
        heading: 'Two different roles',
        body: [
          'For your account — your name, work email, sign-in history and support conversations — '
          + PROVIDER + ' is the controller and decides how that data is used.',
          'For the records your organisation puts into the service — your customers, suppliers, '
          + 'employees — your organisation is the controller and ' + PROVIDER + ' is a processor '
          + 'acting on your instructions. We do not decide what goes in, and we do not use it for '
          + 'our own purposes.',
        ],
      },
      {
        heading: 'What we collect about you',
        body: [
          'Account and contact details, the organisation you belong to, what you are subscribed '
          + 'to, and the technical records a service keeps: sign-in attempts, IP address, browser, '
          + 'and an audit trail of significant actions.',
          'We do not sell personal data, and we do not use your business records to train any '
          + 'model.',
        ],
      },
      {
        heading: 'Why we may process it',
        body: [
          'To provide and secure the service, to bill for it, to support you when you ask, to meet '
          + 'legal obligations, and to tell you about changes that affect your use of it.',
          'Marketing email is sent only where you have agreed, and every such message carries a '
          + 'way to stop it that works immediately.',
        ],
      },
      {
        heading: 'Who else sees it',
        body: [
          'Sub-processors we rely on to run the service — hosting, email delivery, payment '
          + 'processing — each under a written agreement no weaker than this one. A current list '
          + 'is available on request.',
          'Where you switch on the AI assistant, the questions asked and the records used to '
          + 'answer them are sent to the model provider you or we have configured. That is off '
          + 'until an administrator turns it on, and which provider answers is your choice.',
        ],
      },
      {
        heading: 'Where it is held',
        body: [
          'In the region your organisation was created in. We operate in India, Singapore and '
          + 'Malaysia, and your data stays in the region you chose unless you ask us to move it.',
        ],
      },
      {
        heading: 'How long we keep it',
        body: [
          'Your business records for as long as your subscription lasts, and for a defined period '
          + 'afterwards so you can export them. Account and audit records for as long as the law '
          + 'requires. Anything else no longer than it is needed for.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'Access, correction, erasure, restriction, objection and portability, exercised through '
          + PROVIDER + ' where we are the controller, and through your own organisation where it '
          + 'is. Where your organisation is the controller we will pass your request to them and '
          + 'assist them in answering it.',
          '[Placeholder — a real deployment must give the contact address for privacy requests and '
          + 'the supervisory authority in each country of operation.]',
        ],
      },
    ],
  },

  {
    key: 'pdp',
    title: 'Personal data protection',
    updated: '26 August 2026',
    intro:
      'How the platform protects personal data in practice, and what your organisation is '
      + 'responsible for. This sits alongside the privacy policy: that one says what we do, this '
      + 'one says how.',
    sections: [
      {
        heading: 'Separation between customers',
        body: [
          'Every business record carries the organisation it belongs to, and every query is '
          + 'constrained by it in the application, in the data access layer, and again by the '
          + 'database itself. A fault in any one of those three does not expose another '
          + 'customer’s data.',
        ],
      },
      {
        heading: 'Who inside your organisation sees what',
        body: [
          'Access is by role and by record, and can be narrowed to the company, department, team '
          + 'or territory somebody belongs to. Individual fields can be hidden from a role that '
          + 'may otherwise open the record — a salary, a credit limit, a bank account.',
          'Supplier bank details in particular are treated as the redirection-fraud target they '
          + 'are: changing them needs two different people, and the assistant will not read them '
          + 'out under any circumstances.',
        ],
      },
      {
        heading: 'Protection in transit and at rest',
        body: [
          'Everything travels over TLS. Passwords are hashed with a per-user salt and never '
          + 'recoverable. High-sensitivity fields and stored credentials are encrypted with keys '
          + 'held apart from the data, and are never returned by any interface once saved.',
        ],
      },
      {
        heading: 'Audit',
        body: [
          'Sign-ins, permission changes, exports, configuration changes and access to sensitive '
          + 'records are recorded with who, what, when and from where. The trail is append-only '
          + 'and available to your administrators.',
        ],
      },
      {
        heading: 'Support access to your data',
        body: [
          PROVIDER + ' staff cannot read your business records as a matter of course. Where '
          + 'support genuinely needs to, it requires your explicit, time-limited grant, and every '
          + 'action taken under it is recorded and visible to you.',
        ],
      },
      {
        heading: 'What your organisation is responsible for',
        body: [
          'Deciding what personal data to put in and having a lawful basis for it; who to give '
          + 'access to and at what scope; answering the requests of the people whose data it is; '
          + 'and telling us promptly if you believe an account has been compromised.',
        ],
      },
      {
        heading: 'If something goes wrong',
        body: [
          'We will tell you without undue delay about any breach affecting your data, with what we '
          + 'know, what we are doing, and what you may need to do — in time for you to meet your '
          + 'own notification obligations.',
          '[Placeholder — a real deployment must state the notification window each country '
          + 'requires and the contact that receives such a notice.]',
        ],
      },
    ],
  },
];
